import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { STORAGE_KEYS } from '../../modules/auth/constants';
import { Building2, MapPin, Plus, Edit2, Trash2, Search, X, Check, XCircle, Globe, Phone, Mail } from 'lucide-react';
import SuccessToast from '../../components/SuccessToast';

const API_BASE = '/api/v2/organization-locations';

export default function AdminOrganizationLocations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    city: '',
    state: '',
    country_id: '',
    region: '',
    postal_code: ''
  });

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await axios.get(`${API_BASE}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setLocations(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load locations');
      }
    } catch (err) {
      console.error('Load locations error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingLocation(null);
    setFormData({
      city: '',
      state: '',
      country_id: '',
      region: '',
      postal_code: ''
    });
    setShowModal(true);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      city: location.city || '',
      state: location.state || '',
      country_id: location.country_id || '',
      region: location.region || '',
      postal_code: location.postal_code || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await axios.delete(`${API_BASE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSuccessMsg('Location deleted successfully.');
        loadLocations();
      } else {
        setError(response.data.message || 'Failed to delete location');
      }
    } catch (err) {
      console.error('Delete location error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete location');
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    // Status column removed from location_master - this function is no longer needed
    console.warn('Status toggle is not supported in the new HRMS location schema');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      let response;

      const payload = {
        ...formData,
        country_id: formData.country_id === '' || formData.country_id === null ? null : parseInt(formData.country_id, 10)
      };

      if (editingLocation) {
        response = await axios.put(`${API_BASE}/${editingLocation.location_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(API_BASE, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.success) {
        setShowModal(false);
        setSuccessMsg(editingLocation ? 'Location updated successfully.' : 'Location created successfully.');
        loadLocations();
      } else {
        setError(response.data.message || 'Failed to save location');
      }
    } catch (err) {
      console.error('Save location error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save location');
    }
  };

  useEffect(() => {
    loadLocations();
  }, [search]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
        const response = await axios.get(`${API_BASE}/countries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setCountries(response.data.data);
        }
      } catch (err) {
        console.error('Failed to load countries:', err);
      }
    };
    fetchCountries();
  }, []);

  return (
    <div style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", padding: 24, background: '#F1F5F9', minHeight: '100vh' }}>
      <SuccessToast message={successMsg} onClose={() => setSuccessMsg('')} />
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Building2 size={28} color="#0F172A" />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: 0 }}>Organization Locations</h1>
          </div>
          <button
            onClick={handleCreate}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#0F172A', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}
          >
            <Plus size={18} />
            Add Location
          </button>
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={18} color="#94A3B8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search by city, state, or region..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 36px 10px 40px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none' }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#94A3B8' }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>Loading locations...</div>
        ) : locations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1E293B' }}>
              {search ? `No results found for "${search}"` : 'No organization locations found'}
            </div>
            <div style={{ fontSize: 13 }}>
              {search ? 'Try a different search term.' : 'Click "Add Location" to create your first organization location.'}
            </div>
          </div>
        ) : (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>City</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>State</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Region</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Postal Code</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Country</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.location_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: 12, color: '#1E293B', fontWeight: 600 }}>{location.city || '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{location.state || '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{location.region || '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{location.postal_code || '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{countries.find(c => c.country_id === location.country_id)?.country_name || location.country_id || '—'}</td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleEdit(location)}
                          style={{ padding: '6px 10px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#0F172A', cursor: 'pointer' }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(location.location_id)}
                          style={{ padding: '6px 10px', background: '#FEE2E2', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#991B1B', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: 0 }}>
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#64748B' }}
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 6 }}>City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 6 }}>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 6 }}>Country <span style={{ color: '#EF4444' }}>*</span></label>
                    <select
                      required
                      value={formData.country_id}
                      onChange={(e) => setFormData({ ...formData, country_id: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none', cursor: 'pointer' }}
                    >
                      <option value="">Select country</option>
                      {countries.map(c => (
                        <option key={c.country_id} value={c.country_id}>{c.country_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 6 }}>Region</label>
                    <input
                      type="text"
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 6 }}>Postal Code</label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{ padding: '10px 20px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#0F172A', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{ padding: '10px 20px', background: '#0F172A', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}
                  >
                    {editingLocation ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
